import React from "react";
import { ModalProps, RequiredKeys } from "@hex-insights/core";
import { FormType, useFormState } from "@hex-insights/forms";
import { useHistory } from "@hex-insights/router";
import {
	MessageFormValues,
	MessageGroupComponents,
	MessageGroupCreateMutation,
	Student,
	useOrderedLabels,
	User,
	UserGroup,
} from "@hex-insights/verita-romania.shared";
import { AuthenticationContext } from "../../../../Contexts";
import { useViewerStudentNamesAndImages } from "../../../../Utilities";
import { messagingMessageGroupPageInfo } from "../../MessageGroup/pageinfo";

enum Page {
	Recipients,
	Category,
	Topic,
	Message,
	Review,
}

type NewMessageFormValues = Pick<MessageFormValues.Create, "body" | "mediaAttachments">;

const defaultInitialRecipientIDs: User["id"][] = [];

export type NewMessageModalProps = {
	initialRecipientIDs?: User["id"][];
	initialUserCategoryID?: MessageGroupComponents.Create.ParentPortal.MessagingUserCategory["id"] | null;
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

export function NewMessageModal({
	initialRecipientIDs = defaultInitialRecipientIDs,
	initialUserCategoryID = null,
	ifRef,
	onClose,
}: NewMessageModalProps) {
	const { user } = React.useContext(AuthenticationContext);

	const [userRecipientIDs, setUserRecipientIDs] = React.useState<User["id"][]>(initialRecipientIDs);
	const [userGroupRecipientIDs, setUserGroupRecipientIDs] = React.useState<UserGroup["id"][]>([]);
	const [selectedStudentIDs, setSelectedStudentIDs] = React.useState<Student["id"][]>([]);
	const [userCategoryID, setUserCategoryID] = React.useState(initialUserCategoryID);

	const formState = useFormState<NewMessageFormValues>({
		initialFormValues: { body: "", mediaAttachments: [] },
		formType: FormType.Create,
	});

	const { push: pushHistory } = useHistory();
	const onSuccess = React.useCallback(
		(data?: MessageGroupCreateMutation["createMessageGroup"] | null) => {
			if (data) {
				pushHistory(messagingMessageGroupPageInfo.to(data.id));
			}
		},
		[pushHistory],
	);

	const { data: studentData } = useViewerStudentNamesAndImages();
	const availableTopicStudents = React.useMemo(
		() => studentData?.studentConnection.edges.map((e) => e.node) ?? [],
		[studentData],
	);

	const { data: userRecipientData } = MessageGroupComponents.Create.useUserRecipients(); // all possible users
	const availableRecipients = React.useMemo(
		() => userRecipientData?.userConnection.edges.map((e) => e.node) ?? [],
		[userRecipientData],
	);

	const contextValue = React.useMemo<MessageGroupComponents.Create.MessageGroupCreateContextValue>(
		() => ({
			selectedUserRecipients: availableRecipients.filter((e) => userRecipientIDs.includes(e.id)),
			selectedUserGroupRecipients: [],
			selectedTopicStudents: availableTopicStudents.filter((e) => selectedStudentIDs.includes(e.id)),
		}),
		[availableRecipients, userRecipientIDs, availableTopicStudents, selectedStudentIDs],
	);

	const pages = React.useMemo(() => {
		if (userCategoryID) {
			return [Page.Recipients, Page.Category, Page.Topic, Page.Message, Page.Review];
		}
		return [Page.Recipients, Page.Topic, Page.Message, Page.Review];
	}, [userCategoryID]);
	const {
		label: page,
		setLabel: setPage,
		incrementLabel: incrementPage,
		decrementLabel: decrementPage,
	} = useOrderedLabels(pages, () => {
		if (!!initialUserCategoryID) {
			return Page.Category;
		}
		if (initialRecipientIDs.length > 0) {
			return Page.Topic;
		}
		return Page.Recipients;
	});

	React.useEffect(() => {
		if (page === Page.Recipients) {
			setUserCategoryID(null);
		}
	}, [page]);
	const onClickUserCategory = React.useCallback(
		(id: MessageGroupComponents.Create.ParentPortal.MessagingUserCategory["id"]) => {
			setUserRecipientIDs([]);
			setUserGroupRecipientIDs([]);
			setUserCategoryID(id);
			setPage(Page.Category); // instead of incrementPage because that will go to the Topic page due to stale value
		},
		[setPage],
	);

	const MessageRecipientSections = React.useCallback(
		(props: MessageGroupComponents.Create.RecipientSectionsProps) => (
			<MessageGroupComponents.Create.ParentPortal.MessageRecipientSections
				{...props}
				onClickUserCategory={onClickUserCategory}
			/>
		),
		[onClickUserCategory],
	);

	const onUserCategoryBack = React.useCallback(() => {
		setUserCategoryID(null);
		setUserRecipientIDs([]);
		setUserGroupRecipientIDs([]);
		decrementPage();
	}, [decrementPage]);

	const initialPage = (
		<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
			<MessageGroupComponents.Create.NewMessageRecipientsModal
				userRecipientIDs={userRecipientIDs}
				setUserRecipientIDs={setUserRecipientIDs}
				userGroupRecipientIDs={userGroupRecipientIDs}
				setUserGroupRecipientIDs={setUserGroupRecipientIDs}
				topicStudentIDs={selectedStudentIDs}
				setTopicStudentIDs={setSelectedStudentIDs}
				RecipientSections={MessageRecipientSections}
				onNextClick={incrementPage}
				onCancelClick={onClose}
				ifRef={ifRef}
				onClose={onClose}
			/>
		</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
	);

	if (page === Page.Recipients) {
		return initialPage;
	}

	if (page === Page.Category) {
		if (userCategoryID) {
			return (
				<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
					<MessageGroupComponents.Create.ParentPortal.NewMessageUserCategoryRecipientsModal
						userCategoryID={userCategoryID}
						userRecipientIDs={userRecipientIDs}
						setUserRecipientIDs={setUserRecipientIDs}
						onNextClick={incrementPage}
						onBackClick={onUserCategoryBack}
						ifRef={ifRef}
						onClose={onClose}
					/>
				</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
			);
		} else {
			// should not happen but just in case
			return initialPage;
		}
	}

	if (page === Page.Topic) {
		return (
			<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
				<MessageGroupComponents.Create.NewMessageStudentTopicModal
					selectedStudentIDs={selectedStudentIDs}
					setSelectedStudentIDs={setSelectedStudentIDs}
					availableTopicStudents={availableTopicStudents}
					onNextClick={incrementPage}
					onBackClick={decrementPage}
					ifRef={ifRef}
					onClose={onClose}
				/>
			</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
		);
	}

	if (page === Page.Message) {
		return (
			<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
				<MessageGroupComponents.Create.NewMessageMessageModal
					formState={formState}
					userGroupRecipientIDs={userGroupRecipientIDs}
					userRecipientIDs={userRecipientIDs}
					setUserRecipientIDs={setUserRecipientIDs}
					RecipientSections={MessageGroupComponents.Create.ParentPortal.MessageEditRecipientSections}
					onNextClick={incrementPage}
					onBackClick={decrementPage}
					ifRef={ifRef}
					onClose={onClose}
				/>
			</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
		);
	}

	return (
		<MessageGroupComponents.Create.MessageGroupCreateContext.Provider value={contextValue}>
			<MessageGroupComponents.Create.NewMessageReviewModal
				userID={user!.id}
				userRecipientIDs={userRecipientIDs}
				userGroupRecipientIDs={userGroupRecipientIDs}
				selectedStudentIDs={selectedStudentIDs}
				body={formState.formValues.body}
				mediaAttachments={formState.formValues.mediaAttachments}
				onBackClick={decrementPage}
				ifRef={ifRef}
				onClose={onClose}
				onSuccess={onSuccess}
			/>
		</MessageGroupComponents.Create.MessageGroupCreateContext.Provider>
	);
}
